import type { LucideIcon } from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import { Link, matchPath, useLocation } from "react-router";

import { PAGE_BLEED } from "@/components/page";
import { revealScrollLeft } from "@/lib/reveal";
import { cn } from "@/lib/utils";

/** One tab of a page: a part of it with its own path. */
export interface PageTab {
  to: string;
  /** In the dashboard's language. */
  label: string;
  icon: LucideIcon;
  /**
   * Current only on its own path, not on the paths under it: the page's
   * first part, whose path is the page's own (`/analytics`).
   */
  end?: boolean;
  /** The search to open the tab with, such as the period the page shows (`?days=30`). */
  search?: string;
}

/**
 * A tab's look, as a page's tabs and the tabs inside a page share it: its
 * icon and label in a rounded box, the open one in the blue tint and
 * semibold. On a phone the tabs come closer (their icons hide with
 * `TAB_ICON_CLASS`).
 */
export function tabClassName(active: boolean): string {
  return cn(
    "focus-visible:ring-ring flex items-center gap-2.5 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none max-sm:px-2 pointer-coarse:py-2.5",
    active
      ? "bg-accent-light text-primary font-semibold"
      : "text-foreground hover:bg-accent-light hover:text-primary font-medium",
  );
}

/** A tab's icon, left out on a phone. */
export const TAB_ICON_CLASS = "size-4 shrink-0 max-sm:hidden";

/** Whether a tab is the part that is open, from the router's pathname. */
export function isTabCurrent(tab: Pick<PageTab, "to" | "end">, pathname: string): boolean {
  return matchPath({ path: tab.to, end: tab.end ?? false }, pathname) !== null;
}

interface PageTabsProps {
  /** The navigation landmark's accessible name: "Statistieken". */
  label: string;
  tabs: readonly PageTab[];
  className?: string;
}

/**
 * A page's parts as a row of tabs under its title, in the style of the tabs
 * of Settings (`SectionLayout` on a narrow window), at every width: each a
 * link with its icon, the open one in the blue tint with `aria-current`. A
 * tab stays current on the paths under it (an issue under Fouten). On a
 * phone the tabs leave their icons and come closer, so four fit a 390 px
 * screen in every language; on a narrower one they wrap rather than hide a
 * tab off-screen. A wider row that does not fit scrolls sideways, scrolled so
 * the open tab shows.
 *
 *     <PageTabs
 *       label="Statistieken"
 *       tabs={[
 *         { to: "/analytics", label: "Overzicht", icon: LayoutDashboard, end: true },
 *         { to: "/analytics/fouten", label: "Fouten", icon: TriangleAlert },
 *       ]}
 *     />
 */
export function PageTabs({ label, tabs, className }: PageTabsProps) {
  const { pathname } = useLocation();
  const listRef = useRef<HTMLUListElement>(null);
  const labels = tabs.map((tab) => tab.label).join("\n");

  // Bring the open tab into view, sideways only, before the first paint; again after a
  // language switch and whenever the row changes width. Nothing moves when it shows.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    const reveal = () => {
      const current = list.querySelector<HTMLElement>('[aria-current="page"]');
      if (!current || list.scrollWidth <= list.clientWidth) {
        return;
      }
      const start =
        current.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
      const margin = parseFloat(getComputedStyle(list).paddingLeft) || 0;
      list.scrollLeft = revealScrollLeft(
        { start, end: start + current.offsetWidth },
        list.scrollLeft,
        list.clientWidth,
        margin,
      );
    };
    reveal();
    const observer = new ResizeObserver(reveal);
    observer.observe(list);
    return () => observer.disconnect();
  }, [pathname, labels]);

  return (
    <nav aria-label={label} className={className}>
      <ul
        ref={listRef}
        className={cn(
          "scrollbar-themed flex items-center gap-1 overflow-x-auto pb-1 max-sm:flex-wrap max-sm:gap-0.5",
          PAGE_BLEED,
        )}
      >
        {tabs.map((tab) => {
          const active = isTabCurrent(tab, pathname);
          const Icon = tab.icon;
          return (
            <li key={tab.to} className="shrink-0">
              <Link
                to={{ pathname: tab.to, search: tab.search }}
                aria-current={active ? "page" : undefined}
                className={tabClassName(active)}
              >
                <Icon className={TAB_ICON_CLASS} aria-hidden="true" />
                {tab.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

interface TabIntroProps {
  /** What the tab is for, in one or two lines. */
  description: ReactNode;
  /** Controls right of the description, such as "Nieuw team". */
  actions?: ReactNode;
}

/**
 * The top of a tab's content under `<PageTabs>`: what the tab is for, with
 * its controls on the right, in the place of a page's description and
 * actions. No title: the page's title and the open tab already name it. On a
 * narrow window the controls wrap under the description.
 */
export function TabIntro({ description, actions }: TabIntroProps) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <p className="text-muted min-w-0 text-sm">{description}</p>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
